import { createFileRoute, Link } from "@tanstack/react-router";
import { HandHeart, Headphones, Sparkles, Truck } from "lucide-react";
import { Hero } from "@/components/Hero";
import { SectionHeading } from "@/components/SectionHeading";
import { CollectionCard } from "@/components/CollectionCard";
import { ProductCard } from "@/components/ProductCard";
import { EditorialImage } from "@/components/EditorialImage";
import { Suspense } from "react";
import { DeferredSection } from "@/components/DeferredSection";
import { srcSetFor } from "@/lib/image-formats";
import { lazyWithRetry } from "@/lib/lazy-retry";

const EditorialGallery = lazyWithRetry(() =>
  import("@/components/EditorialGallery").then((m) => ({ default: m.EditorialGallery })),
);
const EditorialBannerSlider = lazyWithRetry(() =>
  import("@/components/EditorialBannerSlider").then((m) => ({
    default: m.EditorialBannerSlider,
  })),
);

import { Reveal } from "@/components/Reveal";
import { accessories, lookProducts, newArrivals } from "@/lib/catalog";
import { useAllProducts, useCollectionProducts } from "@/lib/use-catalog";
const beachwearAsset = { url: "/img/beachwear-sunset.webp" };
const shopTheLookAsset = { url: "/img/shop-the-look.webp" };
const maillotsAsset = { url: "/img/maillots-de-bain.webp" };
const kimonosAsset = { url: "/img/kimonos-coverups.webp" };
const sacsAsset = { url: "/img/sacs.webp" };
const bijouxAccessoiresAsset = { url: "/img/bijoux-accessoires.webp" };
const foulardsAsset = { url: "/img/foulards.webp" };
const heroAsset = { url: "/img/hero-sunset.webp" };
const heroMobileAsset = { url: "/img/hero-mobile-sunset.webp" };

export const Route = createFileRoute("/")({
  head: () => ({
    links: [
      { rel: "canonical", href: "https://sunsetconcept.store/" },
      // Négociation de formats : les navigateurs ignorent un preload dont le
      // type MIME n'est pas supporté, le repli WebP prend alors le relais.
      {
        rel: "preload",
        as: "image",
        type: "image/avif",
        href: heroMobileAsset.url.replace(".webp", ".avif"),
        imagesrcset: srcSetFor(heroMobileAsset.url, "avif"),
        imagesizes: "100vw",
        media: "(max-width: 639px)",
        fetchpriority: "high",
      },
      {
        rel: "preload",
        as: "image",
        type: "image/avif",
        href: heroAsset.url.replace(".webp", ".avif"),
        imagesrcset: srcSetFor(heroAsset.url, "avif"),
        imagesizes: "100vw",
        media: "(min-width: 640px)",
        fetchpriority: "high",
      },

    ],
    meta: [
      { title: "Sunset Concept Store — Beachwear, kimonos & resort" },
      {
        name: "description",
        content:
          "Sunset Concept Store, concept store féminin méditerranéen : beachwear, kimonos, sacs et accessoires. Livraison partout au Maroc, paiement à la livraison.",
      },
      { property: "og:title", content: "Sunset Concept Store — Beachwear, kimonos & resort" },
      {
        property: "og:description",
        content: "Une sélection féminine pensée pour vos journées au soleil.",
      },
      { property: "og:url", content: "https://sunsetconcept.store/" },
      { property: "og:image", content: "https://sunsetconcept.store/og-home.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "628" },
      {
        property: "og:image:alt",
        content: "Coucher de soleil méditerranéen sur une côte aux tons terracotta et océan",
      },
      { property: "og:locale", content: "fr_FR" },
      { name: "twitter:title", content: "Sunset Concept Store — Beachwear, kimonos & resort" },
      {
        name: "twitter:description",
        content: "Une sélection féminine pensée pour vos journées au soleil.",
      },
      { name: "twitter:image", content: "https://sunsetconcept.store/og-home.jpg" },
      {
        name: "twitter:image:alt",
        content: "Coucher de soleil méditerranéen sur une côte aux tons terracotta et océan",
      },
    ],
    
  }),
  component: Index,
});

const values = [
  { icon: HandHeart, title: "Paiement à la livraison", text: "Payez à la réception" },
  { icon: Truck, title: "Livraison au Maroc", text: "Livraison partout au Maroc" },
  { icon: Sparkles, title: "Sélection Sunset", text: "Des pièces choisies avec soin" },
  { icon: Headphones, title: "Service client", text: "À votre écoute" },
];

function Index() {
  const featured = useAllProducts(newArrivals);
  const accessoryProducts = useCollectionProducts("/accessoires", accessories, /bijou|accessoire|foulard/i);
  return (
    <>
      <Hero />

      {/* SECTION 2 — NOS UNIVERS */}
      <section className="mx-auto max-w-7xl px-5 py-14 sm:px-8 sm:py-20 lg:px-10 lg:py-28">
        <Reveal>
          <SectionHeading eyebrow="Collections" title="Nos univers" />
        </Reveal>

        <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-5 lg:mt-16 lg:grid-cols-6">
          <Reveal className="lg:col-span-2">
            <CollectionCard
              to="/beachwear"
              title="Beachwear"
              caption="Maillots & pièces solaires"
              image={beachwearAsset.url}
            />
          </Reveal>
          <Reveal delay={80} className="lg:col-span-2">
            <CollectionCard
              to="/maillots"
              title="Maillots de bain"
              caption="Bikinis & une-pièces"
              image={maillotsAsset.url}
              imagePosition="object-[50%_60%]"
              isNew
            />
          </Reveal>
          <Reveal delay={120} className="lg:col-span-2">
            <CollectionCard
              to="/kimonos"
              title="Kimonos & Cover-ups"
              caption="Voiles et lin léger"
              image={kimonosAsset.url}
            />
          </Reveal>
          <Reveal delay={40} className="lg:col-span-2">
            <CollectionCard to="/sacs" title="Sacs" caption="Paniers & cabas d'été" image={sacsAsset.url} />
          </Reveal>
          <Reveal delay={80} className="lg:col-span-2">
            <CollectionCard
              to="/accessoires"
              title="Bijoux & Accessoires"
              caption="Les détails précieux"
              image={bijouxAccessoiresAsset.url}
            />
          </Reveal>
          <Reveal delay={120} className="lg:col-span-2">
            <CollectionCard to="/accessoires" title="Foulards" caption="Soie et imprimés" image={foulardsAsset.url} />
          </Reveal>
        </div>
      </section>

      {/* SECTION 3 — NOUVEAUTÉS */}
      <section className="bg-secondary/50 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
          <Reveal>
            <SectionHeading
              eyebrow="Nouvelle saison"
              title="Les nouveautés Sunset"
              align="left"
            />
          </Reveal>
          <div className="mt-10 grid grid-cols-2 gap-x-4 gap-y-10 sm:gap-x-6 lg:mt-14 lg:grid-cols-4">
            {featured.slice(0, 8).map((p, i) => (
              <Reveal key={p.id} delay={i * 70}>
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>
          <div className="mt-12 flex justify-center">
            <Link to="/nouveautes" className="btn-secondary">
              Voir toutes les nouveautés
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 4 — ÉDITORIAL GALLERY */}
      <section className="mx-auto max-w-7xl py-20 sm:py-24 lg:py-28">
        <Reveal>
          <div className="px-5 sm:px-8 lg:px-10">
            <SectionHeading
              eyebrow="Journal"
              title="L'été en images"
              subtitle="Inspirations et moments capturés entre méditerranée et golden hour."
              align="left"
            />
          </div>
        </Reveal>
        <div className="mt-10">
          <Reveal>
            <DeferredSection
              fallback={
                <div className="-mx-5 flex gap-3 px-5 sm:-mx-8 sm:gap-4 sm:px-8 lg:-mx-10 lg:gap-5 lg:px-10">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="skeleton-shimmer aspect-[4/5] w-[72vw] shrink-0 rounded-sm sm:w-[45vw] lg:w-[30vw]"
                    />
                  ))}
                </div>
              }
            >
              <Suspense fallback={null}>
                <EditorialGallery />
              </Suspense>
            </DeferredSection>
          </Reveal>
        </div>
      </section>

      {/* SECTION 5 — EDITORIAL BANNER SLIDER */}
      <DeferredSection
        fallback={
          <div className="skeleton-shimmer h-[72vh] max-h-[680px] min-h-[480px] w-full bg-sand sm:h-[80svh] sm:max-h-none sm:min-h-[520px]" />
        }
      >
        <Suspense
          fallback={
            <div className="skeleton-shimmer h-[72vh] max-h-[680px] min-h-[480px] w-full bg-sand sm:h-[80svh] sm:max-h-none sm:min-h-[520px]" />
          }
        >
          <EditorialBannerSlider />
        </Suspense>
      </DeferredSection>


      {/* SECTION 5 — SHOP THE LOOK */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:px-10 lg:py-28">
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <div className="group">
              <EditorialImage
                src={shopTheLookAsset.url}
                alt="Look complet Sunset — maillots, chapeau de paille et accessoires dorés"
                ratio="portrait"
                className="aspect-[4/5] sm:aspect-[3/4]"
                imageClassName="object-[50%_40%] sm:object-center"
                sizes="(min-width: 1024px) 45vw, 100vw"
              />
            </div>
          </Reveal>
          <Reveal delay={80}>
            <SectionHeading
              title="Shop the look"
              subtitle="Composez votre look Sunset."
              align="left"
            />
            <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3">
              {lookProducts.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 6 — ACCESSOIRES */}
      <section className="bg-secondary/50 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
          <Reveal>
            <SectionHeading
              title="Les détails qui changent tout"
              subtitle="Complétez votre look."
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-2 gap-x-4 gap-y-10 sm:gap-x-6 lg:grid-cols-4">
            {accessoryProducts.slice(0, 5).map((p, i) => (
              <Reveal key={p.id} delay={i * 70}>
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>
          <div className="mt-12 flex justify-center">
            <Link to="/accessoires" className="btn-secondary">
              Découvrir les accessoires
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 7 — VALEURS */}
      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:px-10">
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-4">
          {values.map(({ icon: Icon, title, text }) => (
            <div key={title} className="flex flex-col items-center text-center">
              <Icon size={22} strokeWidth={1} className="text-accent" />
              <h3 className="label-xs mt-4 text-[0.6rem]">{title}</h3>
              <p className="mt-2 text-xs text-muted-foreground">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION 8 — INSTAGRAM */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:px-10 lg:py-24">
        <Reveal>
          <SectionHeading title="@sunset_concepstore" subtitle="Rejoignez l'univers Sunset" />
        </Reveal>
        <div className="mt-10 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6 lg:gap-3">
          {[
            { src: "/img/insta-1.webp", alt: "Bikini ivoire, chapeau de paille et lunettes sur le sable" },
            { src: "/img/insta-2.webp", alt: "Kimono brodé, panier en paille et chapeau au soleil" },
            { src: "/img/insta-3.webp", alt: "Panier en paille, chapeau et coquillages en lumière dorée" },
            { src: "/img/insta-4.webp", alt: "Bijoux dorés, colliers coquillage et boucles d'oreilles" },
            { src: "/img/insta-5.webp", alt: "Foulard imprimé fleuri et lunettes de soleil sur le sable" },
            { src: "/img/insta-6.webp", alt: "Panier, chapeau et coco sur la plage au coucher du soleil" },
          ].map((item, i) => (
            <a
              key={item.src}
              href="https://www.instagram.com/sunset_concepstore"
              target="_blank"
              rel="noopener noreferrer"
              className="group block"
              aria-label={`Voir la publication ${i + 1} sur Instagram`}
            >
              <EditorialImage
                src={item.src}
                alt={item.alt}
                ratio="square"
                sizes="(min-width: 1024px) 16vw, 33vw"
              />
            </a>
          ))}
        </div>
        <div className="mt-8 text-center">
          <a
            href="https://www.instagram.com/sunset_concepstore"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary"
          >
            SUIVRE SUR INSTAGRAM
          </a>
        </div>

      </section>

      {/* SECTION 9 — NEWSLETTER */}
      <section className="bg-secondary">
        <div className="mx-auto max-w-2xl px-6 py-20 text-center lg:py-24">
          <SectionHeading
            title="Entrez dans l'univers Sunset"
            subtitle="Inscrivez-vous pour découvrir nos nouveautés, collections et inspirations."
          />
          <form
            onSubmit={(e) => e.preventDefault()}
            className="mx-auto mt-9 flex w-full max-w-md flex-col gap-3 sm:flex-row"
          >
            <label htmlFor="newsletter" className="sr-only">
              Adresse e-mail
            </label>
            <input
              id="newsletter"
              type="email"
              required
              placeholder="Votre adresse e-mail"
              className="h-12 flex-1 border border-border bg-background px-4 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-accent"
            />
            <button type="submit" className="btn-primary">
              S'inscrire
            </button>
          </form>
        </div>
      </section>
    </>
  );
}
